import i18n from '../../i18n';
import { documentLinkTarget, parseDocumentLink } from '../../mcp/reveal';
import { bumpDocumentsTree, requestDocumentReveal, useDocumentsUiStore } from '../../extensions/bundled/documents/documentsUiStore';
import { documentsScopesEnabled } from '../../extensions/bundled/documents/documentsFlags';
import { openDocumentTab } from '../../store/tabsStore';
import { setActiveTab } from '../../store/uiModalStore';
import { useNotificationStore } from '../../store/notificationStore';

/**
 * Opens a parsed document link (`parseDocumentLink`): switches the sidebar to
 * the tree that holds the page now (its workspace, whatever the link named),
 * expands its ancestors and opens its tab — or focuses the tab when it is
 * already open (`openDocumentTab` reuses it). `showPanel` also switches the
 * app to Documents, for a link that came from outside (a deep link).
 *
 * Resolves true when the page opened. A page that is not there (deleted, or
 * in a scope this account cannot read) is said so, unless `quietMiss` — the
 * deep-link dispatcher asks quietly first while the account's data may still
 * be settling after sign-in, and says so itself on its last try.
 */
export async function openDocumentFromLink(link, { showPanel = false, quietMiss = false } = {}) {
  if (!link) return false;
  let target = null;
  try {
    target = await documentLinkTarget(link);
  } catch {
    target = null;
  }
  if (!target) {
    if (!quietMiss) warnDocumentLinkMissing();
    return false;
  }
  const { ancestorIds, node, scope } = target;
  // Workspaces off: every personal page is in the one Personal tree.
  const sectionId = documentsScopesEnabled() || !target.sectionId.startsWith('personal/') ? target.sectionId : 'personal';
  const ui = useDocumentsUiStore.getState();
  if (ui.scopeKey !== sectionId) ui.setScopeKey(sectionId);
  requestDocumentReveal(sectionId, node.id, ancestorIds);
  bumpDocumentsTree();
  if (showPanel) setActiveTab('documents.panel');
  openDocumentTab({ documentId: node.id, label: node.name || i18n.t('docSidebar.untitled'), scope });
  return true;
}

export function warnDocumentLinkMissing() {
  useNotificationStore.getState().warning(i18n.t('docSidebar.link.notFound'), i18n.t('docSidebar.link.notFoundTitle'));
}

/**
 * Opens an `apiboo://documents/…` link typed or pasted into the sidebar's
 * search box (card 5.3). Returns false for text that is not a document link
 * (the box keeps filtering), true otherwise — also when the page is gone
 * (said so).
 */
export function openDocumentLinkText(text) {
  const link = parseDocumentLink(text);
  if (!link) return false;
  openDocumentFromLink(link);
  return true;
}
